import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Loader2, AlertTriangle, Repeat, Sun } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { SECTION_COLOR, SECTION_BG } from '../objednavky/ui';
import {
    api, cas, naDatum, naCas, posun, posunOMinuty, rozdilMinut, popisDelky, DELKY, OPAKOVANI, cil,
} from './rezervaceUi';

/**
 * Zadání a úprava rezervace. Kapacita i kolize se přepočítávají živě
 * z `/dostupnost`, ale nic z toho uložení nebrání — rozhoduje člověk.
 *
 * Stavové akce (posadit, nedorazili, zrušit) sem nepatří — jsou v detailu
 * rezervace, kde se dělají jedním klepnutím bez otevírání formuláře.
 */
export default function RezervaceFormular({ rezervace, datum, provoz, isDark, onZavrit, onUlozeno }) {
    const borderColor = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const secondaryColor = 'var(--text-secondary)';
    const inputStyle = {
        width: '100%', minWidth: 0, height: 36, padding: '0 10px', borderRadius: 8, fontSize: 13,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-raised)',
        color: 'var(--text-primary)', outline: 'none', colorScheme: isDark ? 'dark' : 'light',
    };
    const lbl = {
        color: secondaryColor, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.05em', marginBottom: 4, display: 'block',
    };
    const prepinac = (zapnuto) => ({
        borderRadius: 7, cursor: 'pointer', minHeight: 32,
        border: `1px solid ${zapnuto ? SECTION_COLOR : borderColor}`,
        background: zapnuto ? SECTION_BG : 'transparent',
        color: zapnuto ? SECTION_COLOR : secondaryColor,
    });

    const upravuje = Boolean(rezervace?.id);
    const zac = rezervace?.zacatek ? new Date(rezervace.zacatek) : null;
    const kon = rezervace?.konec ? new Date(rezervace.konec) : null;
    const vychoziDelka = provoz?.vychozi_delka_min ?? 120;

    const [celyDen, setCelyDen] = useState(Boolean(rezervace?.cely_den));
    const [den, setDen] = useState(zac ? naDatum(zac) : datum);
    const [hodina, setHodina] = useState(zac ? naCas(zac) : (provoz?.od ?? '18:00'));
    const [denDo, setDenDo] = useState(kon ? naDatum(kon) : datum);
    const [hodinaDo, setHodinaDo] = useState(
        kon ? naCas(kon) : posunOMinuty(datum, provoz?.od ?? '18:00', vychoziDelka)[1]
    );
    // Opakování platí jen při zakládání — úprava mění jeden termín, ne pravidlo.
    const [opakovani, setOpakovani] = useState('zadne');
    const [opakovatDo, setOpakovatDo] = useState(() => posun(datum, 28));
    const [pocetHostu, setPocetHostu] = useState(rezervace?.pocet_hostu ?? 2);
    const [jmeno, setJmeno] = useState(rezervace?.jmeno ?? '');
    const [telefon, setTelefon] = useState(rezervace?.telefon ?? '');
    const [poznamka, setPoznamka] = useState(rezervace?.poznamka ?? '');
    const [zonaId, setZonaId] = useState(rezervace?.cela_sekce ? rezervace.zona?.id : null);
    const [stulIds, setStulIds] = useState(rezervace && !rezervace.cela_sekce ? (rezervace.stoly || []).map(s => s.id) : []);
    const [rezim, setRezim] = useState(rezervace?.cela_sekce ? 'sekce' : 'stoly');

    const [dostupnost, setDostupnost] = useState(null);
    const [pracuje, setPracuje] = useState(false);
    const [chyba, setChyba] = useState(null);

    // Celý den se posílá jako datum bez času — hodiny doplní server z provozní
    // doby, ať se to při změně otevíračky nerozejde s tím, co je v datech.
    const zacatek = celyDen ? den : `${den} ${hodina}:00`;
    const konec = celyDen ? denDo : `${denDo} ${hodinaDo}:00`;
    const delkaMin = celyDen ? null : rozdilMinut(den, hodina, denDo, hodinaDo);
    const viceDnu = den !== denDo;
    const oknoSedi = celyDen ? denDo >= den : delkaMin >= 15;

    /** Rychlá délka jen přepíše konec — nic skrytého, je vidět, co se uloží. */
    const nastavitDelku = (minut) => {
        const [d, h] = posunOMinuty(den, hodina, minut);
        setDenDo(d);
        setHodinaDo(h);
    };

    // Posun začátku táhne konec s sebou, aby okno nezůstalo obrácené.
    const zmenitZacatek = (novyDen, novaHodina) => {
        const delka = Math.max(15, rozdilMinut(den, hodina, denDo, hodinaDo) || vychoziDelka);
        setDen(novyDen);
        setHodina(novaHodina);
        const [d, h] = posunOMinuty(novyDen, novaHodina, delka);
        setDenDo(d);
        setHodinaDo(h);
    };

    useEffect(() => {
        let zruseno = false;
        const id = setTimeout(async () => {
            try {
                const q = new URLSearchParams({ zacatek, konec, cely_den: celyDen ? '1' : '0' });
                if (rezervace?.id) q.set('krome_id', String(rezervace.id));
                const d = await api(`/api/pos/rezervace/dostupnost?${q}`);
                if (!zruseno) setDostupnost(d);
            } catch {
                // Bez dostupnosti se rezervace pořád uloží, jen bez živého
                // varování — server si to ohlídá sám.
            }
        }, 300);
        return () => { zruseno = true; clearTimeout(id); };
    }, [zacatek, konec, celyDen, rezervace?.id]);

    const sekce = dostupnost?.sekce ?? [];
    const stoly = dostupnost?.stoly ?? [];

    const kapacita = zonaId
        ? (sekce.find(s => s.id === zonaId)?.kapacita ?? 0)
        : stoly.filter(s => stulIds.includes(s.id)).reduce((n, s) => n + (s.kapacita || 0), 0);
    const malo = kapacita > 0 && pocetHostu > kapacita;

    const kolize = useMemo(() => {
        const dle = new Map();
        if (zonaId) {
            for (const k of sekce.find(s => s.id === zonaId)?.kolize ?? []) dle.set(k.rezervace_id, k);
        } else {
            for (const s of stoly) {
                if (!stulIds.includes(s.id)) continue;
                for (const k of s.kolize ?? []) dle.set(k.rezervace_id, k);
            }
        }
        return [...dle.values()];
    }, [sekce, stoly, zonaId, stulIds]);

    const ulozit = async () => {
        if (pracuje) return;
        setPracuje(true);
        setChyba(null);
        try {
            const payload = {
                zacatek, konec, cely_den: celyDen, pocet_hostu: pocetHostu,
                jmeno: jmeno.trim(), telefon: telefon.trim() || null,
                poznamka: poznamka.trim() || null,
                zona_id: zonaId, stul_ids: stulIds, zdroj: 'os',
                ...(upravuje || opakovani === 'zadne' ? {} : { opakovani, opakovat_do: opakovatDo }),
            };
            const res = upravuje
                ? await api(`/api/pos/rezervace/${rezervace.id}`, 'PATCH', payload)
                : await api('/api/pos/rezervace', 'POST', payload);
            onUlozeno(res.varovani || [], res.serie || null, res.rezervace || null);
        } catch (e) {
            setChyba(e.message || 'Nepodařilo se uložit.');
        } finally {
            setPracuje(false);
        }
    };

    const nelze = (!zonaId && !stulIds.length) || !jmeno.trim() || !oknoSedi || pracuje;

    return (
        <ModalShell onClose={onZavrit} isDark={isDark} barva={SECTION_COLOR} ikona={CalendarClock} sirka={620}
            nadpis={upravuje ? `Upravit rezervaci · ${cil(rezervace)}` : 'Nová rezervace'}
            paticka={(
                <div className="flex flex-col" style={{ gap: 8 }}>
                    {chyba && <div className="text-xs" style={{ color: '#EF4444' }}>{chyba}</div>}
                    <button type="button" onClick={ulozit} disabled={nelze}
                        className="w-full rounded-xl font-semibold inline-flex items-center justify-center"
                        style={{ height: 44, gap: 6, background: SECTION_COLOR, color: '#fff', border: 'none', opacity: nelze ? 0.5 : 1, cursor: nelze ? 'not-allowed' : 'pointer' }}>
                        {pracuje && <Loader2 size={15} className="animate-spin" />}
                        {upravuje ? 'Uložit změny' : 'Uložit rezervaci'}
                    </button>
                </div>
            )}>
            <div className="flex flex-col" style={{ padding: 16, gap: 14 }}>
                <div className="flex items-center justify-between">
                    <label style={{ ...lbl, marginBottom: 0 }}>Kdy</label>
                    <button type="button" onClick={() => setCelyDen(v => !v)}
                        className="flex items-center gap-1.5 px-2.5 text-[11px] font-semibold" style={prepinac(celyDen)}>
                        <Sun size={12} /> Celý den
                    </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="min-w-0">
                        <label style={lbl}>Od</label>
                        <div className="flex gap-1.5">
                            <input type="date" value={den} onChange={e => zmenitZacatek(e.target.value, hodina)} style={inputStyle} />
                            {!celyDen && (
                                <input type="time" value={hodina} step={(provoz?.krok_minut ?? 15) * 60}
                                    onChange={e => zmenitZacatek(den, e.target.value)} style={{ ...inputStyle, maxWidth: 110 }} />
                            )}
                        </div>
                    </div>
                    <div className="min-w-0">
                        <label style={lbl}>Do</label>
                        <div className="flex gap-1.5">
                            <input type="date" value={denDo} min={den} onChange={e => setDenDo(e.target.value)} style={inputStyle} />
                            {!celyDen && (
                                <input type="time" value={hodinaDo} step={(provoz?.krok_minut ?? 15) * 60}
                                    onChange={e => setHodinaDo(e.target.value)} style={{ ...inputStyle, maxWidth: 110 }} />
                            )}
                        </div>
                    </div>
                </div>

                {celyDen ? (
                    <div className="text-[11px]" style={{ color: mutedColor }}>
                        Blokuje celou provozní dobu ({provoz?.od}–{provoz?.do}){viceDnu ? ', každý den v rozmezí' : ''}.
                    </div>
                ) : (
                    <div className="flex items-center gap-1.5">
                        {DELKY.map(d => (
                            <button key={d} type="button" onClick={() => nastavitDelku(d)}
                                className="flex-1 text-xs font-semibold" style={prepinac(delkaMin === d)}>
                                {d >= 60 ? `${d / 60} h` : `${d} min`}
                            </button>
                        ))}
                        <span className="text-[11px] pl-1" style={{ color: oknoSedi ? mutedColor : '#EF4444', whiteSpace: 'nowrap' }}>
                            {oknoSedi ? popisDelky(delkaMin) : 'konec je před začátkem'}
                        </span>
                    </div>
                )}

                {upravuje ? (
                    rezervace.serie_id && (
                        <div className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-[11px]"
                            style={{ background: SECTION_BG, color: secondaryColor }}>
                            <Repeat size={12} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                            Termín opakované rezervace. Změny se uloží jen k tomuhle termínu.
                        </div>
                    )
                ) : (
                    <div>
                        <label style={lbl}>Opakovat</label>
                        <div className="flex flex-wrap gap-1.5">
                            {OPAKOVANI.map(o => (
                                <button key={o.id} type="button" onClick={() => setOpakovani(o.id)}
                                    className="flex-1 text-[11px] font-semibold" style={{ ...prepinac(opakovani === o.id), padding: '0 8px', whiteSpace: 'nowrap' }}>
                                    {o.label}
                                </button>
                            ))}
                        </div>
                        {opakovani !== 'zadne' && (
                            <div className="flex items-center gap-2 mt-2">
                                <span className="text-[11px]" style={{ color: secondaryColor }}>do</span>
                                <input type="date" value={opakovatDo} min={den}
                                    onChange={e => setOpakovatDo(e.target.value)} style={{ ...inputStyle, maxWidth: 190 }} />
                            </div>
                        )}
                    </div>
                )}

                <div className="grid gap-3" style={{ gridTemplateColumns: '84px minmax(0,1fr) minmax(0,1fr)' }}>
                    <div>
                        <label style={lbl}>Hostů</label>
                        <input type="number" inputMode="numeric" min={1} max={200} value={pocetHostu}
                            onChange={e => setPocetHostu(Math.max(1, Number(e.target.value) || 1))} style={inputStyle} />
                    </div>
                    <div className="min-w-0">
                        <label style={lbl}>Na jméno</label>
                        <input value={jmeno} onChange={e => setJmeno(e.target.value)} placeholder="Novák" style={inputStyle} />
                    </div>
                    <div className="min-w-0">
                        <label style={lbl}>Telefon</label>
                        <input type="tel" value={telefon} onChange={e => setTelefon(e.target.value)} placeholder="777 123 456" style={inputStyle} />
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1">
                        <label style={{ ...lbl, marginBottom: 0 }}>Kam</label>
                        <div className="flex gap-1">
                            {[{ id: 'sekce', label: 'Celá sekce' }, { id: 'stoly', label: 'Stoly' }].map(v => (
                                <button key={v.id} type="button"
                                    onClick={() => { setRezim(v.id); setZonaId(null); setStulIds([]); }}
                                    className="px-2.5 text-[11px] font-semibold" style={prepinac(rezim === v.id)}>
                                    {v.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {rezim === 'sekce' ? (
                        <div className="grid grid-cols-2 gap-1.5">
                            {sekce.map(s => (
                                <Volba key={s.id} vybrano={zonaId === s.id} obsazeno={s.kolize.length > 0}
                                    borderColor={borderColor} onClick={() => { setZonaId(s.id); setStulIds([]); }}>
                                    <div className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{s.nazev}</div>
                                    <div className="text-[10px]" style={{ color: mutedColor }}>zbývá {s.zbyva} z {s.kapacita} míst</div>
                                </Volba>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {sekce.map(sek => {
                                const jejiStoly = stoly.filter(s => s.zona_id === sek.id);
                                if (!jejiStoly.length) return null;
                                return (
                                    <div key={sek.id}>
                                        <div className="text-[10px] font-bold uppercase mb-1" style={{ color: mutedColor, letterSpacing: '0.05em' }}>{sek.nazev}</div>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                                            {jejiStoly.map(s => (
                                                <Volba key={s.id} vybrano={stulIds.includes(s.id)}
                                                    obsazeno={!s.volny || s.ma_ucet} borderColor={borderColor}
                                                    onClick={() => setStulIds(p => p.includes(s.id) ? p.filter(x => x !== s.id) : [...p, s.id])}>
                                                    <div className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{s.nazev || `Stůl ${s.cislo}`}</div>
                                                    <div className="text-[10px]" style={{ color: mutedColor }}>{s.kapacita} míst{!s.volny ? ' · rezervace' : ''}</div>
                                                </Volba>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {kapacita > 0 && (
                        <div className="text-[11px] mt-1.5" style={{ color: malo ? '#F59E0B' : mutedColor }}>
                            {malo
                                ? `Vybraná místa mají kapacitu ${kapacita}, hostů je ${pocetHostu}. Uložit to jde.`
                                : `Kapacita výběru: ${kapacita} míst`}
                        </div>
                    )}
                </div>

                <div>
                    <label style={lbl}>Poznámka pro obsluhu</label>
                    <textarea value={poznamka} onChange={e => setPoznamka(e.target.value)} rows={2}
                        placeholder="Dort s sebou, alergie, kočárek, platí firma…"
                        style={{ ...inputStyle, height: 'auto', padding: '8px 10px', resize: 'none' }} />
                </div>

                {kolize.length > 0 && (
                    <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold mb-1" style={{ color: '#F59E0B' }}>
                            <AlertTriangle size={12} /> V tuhle dobu se to potká s jinou rezervací
                        </div>
                        {kolize.map(k => (
                            <div key={k.rezervace_id} className="text-[11px] pl-4" style={{ color: secondaryColor }}>
                                {k.jmeno} · {k.pocet_hostu} hostů · {cas(k.zacatek)}–{cas(k.konec)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </ModalShell>
    );
}

function Volba({ vybrano, obsazeno, borderColor, onClick, children }) {
    const barva = vybrano ? SECTION_COLOR : obsazeno ? '#F59E0B' : null;
    return (
        <button type="button" onClick={onClick} className="text-left px-2 py-1.5 min-w-0"
            style={{
                borderRadius: 7, cursor: 'pointer', minHeight: 40,
                border: `1px solid ${barva || borderColor}`,
                background: barva ? `${barva}1f` : 'transparent',
            }}>
            {children}
        </button>
    );
}
